/**
 * OrganizationPoolWidget - Виджет "Пул организаций"
 *
 * Показывает нераспределённые организации для взятия в работу.
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Stack,
  Group,
  Text,
  Title,
  Box,
  Loader,
  Center,
  Badge,
  Button,
  TextInput,
  Progress,
  ThemeIcon,
} from '@mantine/core';
import {
  IconBuildingCommunity,
  IconSearch,
  IconStar,
  IconPlus,
} from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { notifications } from '@mantine/notifications';
import tokens from '../../theme/designTokens';

const OrganizationItem = ({ org, onClick, onAssign }) => {
  return (
    <Group
      p="xs"
      style={{
        borderRadius: '8px',
        backgroundColor: tokens.colors.neutral[1],
        transition: 'background-color 150ms ease',
      }}
      onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = tokens.colors.neutral[2])}
      onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = tokens.colors.neutral[1])}
    >
      <Box
        style={{ flex: 1, minWidth: 0, cursor: 'pointer' }}
        onClick={onClick}
      >
        <Text size="sm" fw={500} truncate>
          {org.name}
        </Text>
        <Group gap="xs" mt={2}>
          <Text size="xs" c="dimmed">
            {org.category}
          </Text>
          {org.rating && (
            <Badge
              size="xs"
              variant="light"
              color="yellow"
              radius="xl"
              leftSection={<IconStar size={10} />}
            >
              {org.rating.toFixed(1)}
            </Badge>
          )}
        </Group>
      </Box>
      <Button
        size="xs"
        variant="light"
        color="green"
        leftSection={<IconPlus size={12} />}
        onClick={(e) => {
          e.stopPropagation();
          onAssign(org);
        }}
      >
        Взять
      </Button>
    </Group>
  );
};

const OrganizationPoolWidget = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [assigning, setAssigning] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: '1',
        page_size: '5',
      });
      if (search) {
        params.set('category', search);
      }
      const response = await fetch(`/api/dashboard/sales/pool?${params}`, {
        credentials: 'include',
      });
      if (response.ok) {
        const result = await response.json();
        setData(result);
      }
    } catch (err) {
      console.error('Failed to load organization pool:', err);
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleOrgClick = (org) => {
    navigate(`/organizations/${org.id}`);
  };

  const handleAssign = async (org) => {
    try {
      setAssigning(true);
      const response = await fetch(`/api/organizations/${org.id}/assign-sales`, {
        method: 'POST',
        credentials: 'include',
      });
      if (response.ok) {
        // Обновляем список
        await fetchData();
        notifications.show({
          title: 'Успешно',
          message: `Организация "${org.name}" взята в работу`,
          color: 'green',
        });
      } else {
        const error = await response.json();
        notifications.show({
          title: 'Ошибка',
          message: error.detail || 'Не удалось взять организацию',
          color: 'red',
        });
      }
    } catch (err) {
      console.error('Failed to assign organization:', err);
      notifications.show({
        title: 'Ошибка',
        message: 'Ошибка при назначении организации',
        color: 'red',
      });
    } finally {
      setAssigning(false);
    }
  };

  const limitProgress = data
    ? (data.my_organizations_count / data.my_limit) * 100
    : 0;

  return (
    <Card shadow="sm" radius={16} withBorder p="lg" h="100%">
      <Stack gap="md">
        {/* Заголовок */}
        <Group justify="space-between">
          <Group gap="xs">
            <IconBuildingCommunity size={20} style={{ color: tokens.colors.brand.primary }} />
            <Title order={4}>Пул организаций</Title>
          </Group>
          {data && (
            <Badge color="blue" variant="light" radius="xl" size="sm">
              {data.total}
            </Badge>
          )}
        </Group>

        {/* Мой лимит */}
        {data && (
          <Box>
            <Group justify="space-between" mb={4}>
              <Text size="xs" c="dimmed">
                Мой лимит: {data.my_organizations_count} / {data.my_limit}
              </Text>
            </Group>
            <Progress
              value={limitProgress}
              size="xs"
              color={limitProgress >= 90 ? 'red' : limitProgress >= 70 ? 'yellow' : 'green'}
            />
          </Box>
        )}

        {/* Поиск */}
        <TextInput
          size="xs"
          placeholder="Поиск по категории..."
          leftSection={<IconSearch size={14} />}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        {/* Список организаций */}
        {loading ? (
          <Center py="md">
            <Loader size="sm" />
          </Center>
        ) : data?.items?.length > 0 ? (
          <Stack gap="xs">
            {data.items.map((org) => (
              <OrganizationItem
                key={org.id}
                org={org}
                onClick={() => handleOrgClick(org)}
                onAssign={handleAssign}
              />
            ))}
          </Stack>
        ) : (
          <Center py="md">
            <Text size="sm" c="dimmed">
              {search ? 'Организации не найдены' : 'Пул пуст'}
            </Text>
          </Center>
        )}

        {/* Ссылка на полный список */}
        {data?.total > 5 && (
          <Button
            variant="subtle"
            size="xs"
            fullWidth
            onClick={() => navigate('/organizations?filter=unassigned')}
          >
            Показать все ({data.total})
          </Button>
        )}
      </Stack>
    </Card>
  );
};

export default OrganizationPoolWidget;
